import type { BenchmarkRun } from '../../types/benchmark';

interface RunSelectorProps {
  selectedRun: BenchmarkRun | undefined;
  onClick: () => void;
  onViewFirstDocument?: () => void;
}

export function RunSelector({ selectedRun, onClick, onViewFirstDocument: _onViewFirstDocument }: RunSelectorProps) {
  void _onViewFirstDocument; // Reserved for future use
  if (!selectedRun) {
    return (
      <div
        onClick={onClick}
        className="border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Aucun run sélectionné - Cliquez pour sélectionner</span>
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="border-2 border-gray-300 rounded-lg p-4 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors bg-white"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* First line: Run ID, note, model, F1, tags */}
          <div className="flex items-center gap-4 text-sm mb-2">
            <span className="font-medium text-gray-700">
              Run ({selectedRun.id.slice(0, 8)}): {selectedRun.is_locked && '🔒 '}
              {selectedRun.note || 'Sans note'}
            </span>
            <span className="text-gray-500">{selectedRun.model_name}</span>
            <span className="text-gray-600">
              (F1: {selectedRun.avg_f1 !== null && selectedRun.avg_f1 !== undefined
                ? Number(selectedRun.avg_f1).toFixed(3)
                : 'N/A'})
            </span>
            {selectedRun.tags && selectedRun.tags.length > 0 && (
              <span className="flex gap-1">
                {selectedRun.tags.slice(0, 2).map((tag, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                    {tag}
                  </span>
                ))}
                {selectedRun.tags.length > 2 && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                    +{selectedRun.tags.length - 2}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Second line: Domain and versions (right-aligned) */}
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span className="font-medium">{selectedRun.domain}</span>
            <div className="flex items-center gap-2 text-gray-400">
              <span>Prompt: v{selectedRun.prompt?.version || selectedRun.prompt_version || '-'}</span>
              <span>|</span>
              <span>Schéma: v{selectedRun.schema?.version || '-'}</span>
              <span>|</span>
              <span>LLM: v{selectedRun.llm_config?.version || '-'}</span>
              <span>|</span>
              <span>OCR: v{selectedRun.ocr_config?.version || '-'}</span>
            </div>
          </div>
        </div>

        <svg className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}
